import { Link } from '@tanstack/react-router';
import {
  CloudIcon,
  Link2,
  LogsIcon,
  MessageSquareIcon,
  SearchIcon,
  SparklesIcon,
} from 'lucide-react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface NoFilteredIncidentsProps {
  onClearFilters: () => void;
}

export const EmptyIncidents = (): JSX.Element => {
  const { toast } = useToast();
  const [isFetching, setIsFetching] = useState(false);

  const handleFetchPastIncidents = async () => {
    setIsFetching(true);

    try {
      // TODO: Replace with actual API call when backend is ready
      // await fetchBackend('/incidents/fetch-from-slack', { method: 'POST' });

      // Mock delay for demonstration
      await new Promise(resolve => setTimeout(resolve, 2000));

      toast({
        // @ts-expect-error we should use a translationkey
        title: 'Scanning Slack History',
        // @ts-expect-error we should use a translationkey
        description:
          'Annie is analyzing your Slack channels for past incidents. This may take a few minutes.',
      });

      // TODO: Poll for completion or use WebSocket for real-time updates
      setTimeout(() => {
        toast({
          // @ts-expect-error we should use a translationkey
          title: 'Import Complete',
          // @ts-expect-error we should use a translationkey
          description:
            'Found 3 past incidents. Annie is now investigating them.',
        });
      }, 5000);
    } catch (error) {
      console.error('Failed to fetch past incidents:', error);
      toast({
        // @ts-expect-error we should use a translationkey
        title: 'Import Failed',
        // @ts-expect-error we should use a translationkey
        description:
          'Unable to scan Slack history. Please try again or contact support.',
        variant: 'destructive',
      });
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center max-w-4xl mx-auto">
      {/* Icon and Header */}
      <div className="flex items-center justify-center w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
        <SparklesIcon className="w-8 h-8 text-blue-600 dark:text-blue-400" />
      </div>

      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
        Annie is eager to investigate its first alert
      </h2>

      {/* Getting Started Actions */}
      <div className="grid md:grid-cols-2 gap-6 w-full mb-12 max-w-2xl">
        {/* Tag Annie Card */}
        <div className="p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl border border-blue-200/50 dark:border-blue-700/50">
          <div className="flex items-center mb-3">
            <MessageSquareIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Tag Annie in Slack
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            When an alert or incident occurs, mention{' '}
            <span className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">
              @annie
            </span>{' '}
            in your Slack channel to start an investigation.
          </p>
          <div className="text-xs text-gray-500 dark:text-gray-400 font-mono bg-white dark:bg-gray-900 p-2 rounded border">
            @annie investigate this alert
          </div>
        </div>

        {/* Fetch Past Incidents Card */}
        <div className="p-6 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl border border-purple-200/50 dark:border-purple-700/50">
          <div className="flex items-center mb-3">
            <SearchIcon className="w-5 h-5 text-purple-600 dark:text-purple-400 mr-2" />
            <h3 className="font-medium text-gray-900 dark:text-white">
              Import Past Incidents
            </h3>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
            Let Annie scan your Slack history to find and investigate past
            incidents automatically.
          </p>
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleFetchPastIncidents}
            disabled={isFetching}
          >
            <SearchIcon
              className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            {isFetching ? 'Scanning...' : 'Fetch Past Incidents'}
          </Button>
        </div>
      </div>

      {/* Cloud Context Section */}
      <div className="w-full max-w-2xl bg-gray-50 dark:bg-gray-800/50 rounded-xl p-6">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-medium text-gray-900 dark:text-white mb-2">
            💡 Connect integrations for deeper insights
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Annie provides much better analysis with access to your cloud
            providers and monitoring tools.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Link to="/integrations/aws">
            <Button variant="outline" size="sm" className="w-full">
              <CloudIcon className="w-4 h-4 mr-1" />
              AWS
            </Button>
          </Link>
          <Link to="/integrations/gcp">
            <Button variant="outline" size="sm" className="w-full">
              <CloudIcon className="w-4 h-4 mr-1" />
              GCP
            </Button>
          </Link>
          <Link to="/integrations/monitoring/datadog-api-credentials">
            <Button variant="outline" size="sm" className="w-full">
              <LogsIcon className="w-4 h-4 mr-1" />
              Datadog
            </Button>
          </Link>
          <Link to="/integrations">
            <Button variant="outline" size="sm" className="w-full">
              <Link2 className="w-4 h-4 mr-1" />
              See all
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export const NoFilteredIncidents = ({
  onClearFilters,
}: NoFilteredIncidentsProps): JSX.Element => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="text-gray-500 dark:text-gray-400 mb-4">
      <FormattedMessage id="incidents.noFilteredIncidents" />
    </div>
    <Button onClick={onClearFilters} variant="outline">
      <FormattedMessage id="incidents.filters.clear" />
    </Button>
  </div>
);
